import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import type { AuthMeResponse } from '@lds/shared'

import { api, ApiError } from './api'

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; me: AuthMeResponse }
  | { status: 'anonymous' }

type AuthCtxValue = AuthState & { refresh: () => Promise<void> }

const AuthCtx = createContext<AuthCtxValue>({
  status: 'loading',
  refresh: async () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  const refresh = useCallback(async () => {
    try {
      const me = await api.authMe()
      setState({ status: 'authenticated', me })
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setState({ status: 'anonymous' })
      else throw e
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const value = useMemo<AuthCtxValue>(() => ({ ...state, refresh }), [state, refresh])

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>
}

export function useAuth() {
  return useContext(AuthCtx)
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth()
  const loc = useLocation()
  if (auth.status === 'loading') return <div>Chargement…</div>
  if (auth.status === 'anonymous')
    return <Navigate to={`/auth/sign-in?next=${encodeURIComponent(loc.pathname)}`} replace />
  return <>{children}</>
}
